import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Briefcase, Users, QrCode, Armchair } from 'lucide-react';

function HeroSection({ company }) {
  return (
    <section className="min-h-[calc(100vh-5rem)] flex items-center justify-center relative overflow-hidden px-6">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-kore-500/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-accent-lavender/20 rounded-full blur-[100px]" />
        <div className="absolute inset-0 noise pointer-events-none" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* Logo */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: 'spring', duration: 0.8 }}
          className="inline-flex items-center justify-center w-24 h-24 mb-8 rounded-3xl bg-gradient-to-br from-kore-400 via-kore-500 to-kore-700 shadow-2xl shadow-kore-500/30"
        >
          <span className="text-5xl font-bold text-white">K</span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
        >
          <span className="text-gradient">KoreFolio</span>
        </motion.h1>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-xl md:text-2xl lg:text-3xl text-zinc-300 mb-4 font-light"
        >
          The Professional Network for the Beauty Industry
        </motion.p>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-lg md:text-xl text-zinc-500 max-w-3xl mx-auto mb-12"
        >
          Instantly connecting employers with talent while building professional careers through engaging, social-first experiences
        </motion.p>

        {/* Feature Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-wrap items-center justify-center gap-4 mb-12"
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-kore-500/10 border border-kore-500/20 rounded-full">
            <Briefcase className="w-4 h-4 text-kore-400" />
            <span className="text-sm text-kore-300">Job Connect</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-accent-lavender/10 border border-accent-lavender/20 rounded-full">
            <Users className="w-4 h-4 text-accent-lavender" />
            <span className="text-sm text-purple-300">Professional Network</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-accent-coral/10 border border-accent-coral/20 rounded-full">
            <QrCode className="w-4 h-4 text-accent-coral" />
            <span className="text-sm text-red-300">KoreCode Sharing</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-accent-mint/10 border border-accent-mint/20 rounded-full">
            <Armchair className="w-4 h-4 text-accent-mint" />
            <span className="text-sm text-green-300">Chair-Swapping</span>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="flex items-center justify-center gap-4"
        >
          <div className="text-zinc-500 text-sm">
            Scroll or use arrow keys to navigate
          </div>
          <ArrowRight className="w-5 h-5 text-zinc-500 animate-pulse" />
        </motion.div>

        {/* Stats Preview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
        >
          {[
            { value: '$75B+', label: 'Market Size' },
            { value: '650K+', label: 'Professionals' },
            { value: '$500K', label: 'Seeking' },
            { value: '45 Days', label: 'To MVP' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + index * 0.1 }}
              className="text-center"
            >
              <div className="text-2xl md:text-3xl font-bold text-gradient">{stat.value}</div>
              <div className="text-sm text-zinc-500">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default HeroSection;
